import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import IssueReportApi from '@/api/issueReportApi';
import { IssueReportCreate, IssueReportQuery, IssueReportUpdate } from '@/schema/issueReport';

export const useIssueReports = (page: number, size: number, query: IssueReportQuery) =>
  useQuery({
    queryKey: ['issue-reports', page, size, query],
    queryFn: () => IssueReportApi.getAll(page, size, query),
  });

export const useIssueReport = (id?: string) =>
  useQuery({
    queryKey: ['issue-report', id],
    queryFn: () => IssueReportApi.getById(id!),
    enabled: Boolean(id),
  });

export const useSubmitIssueReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IssueReportCreate) => IssueReportApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issue-reports'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
};

export const useDeleteIssueReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => IssueReportApi.delete(id),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ['issue-report', id] });
      queryClient.invalidateQueries({ queryKey: ['issue-reports'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
};

export const useUpdateIssueReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IssueReportUpdate }) =>
      IssueReportApi.update(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['issue-reports'] });
      queryClient.invalidateQueries({ queryKey: ['issue-report', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
};
